import { useMutation } from '@tanstack/react-query';

import { sendCode } from '../../api/passcode.api';
import { liveHoldOf, resumableSession } from '../../domain/codeChallenge';
import { codeEffectOf } from '../../domain/codeEffect';
import { isIdentifierRefusal, isTransportFailure, toRefusal } from '../../domain/refusal';
import type { PasscodeChannel } from '../../types/domain.types';

import { errorOf } from './loginFailures';
import { acceptsSend, askKeyOf } from './loginMachine';
import type { CodeAsk } from './loginMachine';
import { passcodeMutationKeys } from './mutationKeys';
import { clearSettled } from './useLoginMachine';
import type { LoginMachine } from './useLoginMachine';

interface SendInput extends CodeAsk {
  readonly epoch: number;
  readonly fromField: boolean;
}

export interface CodeSendLogic {
  readonly pending: boolean;
  readonly channel: PasscodeChannel | null;
  readonly paused: boolean;
  readonly error: Error | null;
  readonly request: (ask: CodeAsk, fromField: boolean) => void;
  readonly retryLast: () => boolean;
  readonly clear: () => void;
}

export interface CodeSendOptions {
  readonly onCodeSent?: () => void;
}

const askOf = ({ identifier, purpose, channel }: SendInput): CodeAsk => ({ identifier, purpose, channel });

export const useCodeSend = (machine: LoginMachine, { onCodeSent }: CodeSendOptions): CodeSendLogic => {
  const { dispatch, latest, inFlight } = machine;
  const sender = useMutation({
    mutationKey: passcodeMutationKeys.send(),
    mutationFn: ({ identifier, channel, purpose }: SendInput) =>
      sendCode({ identifier, channel, purpose: purpose === 'LOGIN' ? undefined : purpose }, undefined),
  });

  const onRefused = (failure: Error, input: SendInput): void => {
    const { hold } = codeEffectOf(failure);
    const ask = askOf(input);
    const at = Date.now();
    if (hold?.kind === 'locked') {
      dispatch({ type: 'locked', epoch: input.epoch, ask, seconds: hold.seconds, at });
      return;
    }
    if (hold !== null && hold.reason !== 'LOCKED') {
      const now = latest();
      const resumes =
        input.fromField &&
        hold.reason === 'RESEND_TOO_SOON' &&
        now.epoch === input.epoch &&
        resumableSession(now.challenge, askKeyOf(ask), ask.channel, at) !== null;
      dispatch({ type: 'sendHeld', epoch: input.epoch, ask, reason: hold.reason, seconds: hold.seconds, at });
      if (resumes) onCodeSent?.();
      return;
    }
    if (input.fromField && !isTransportFailure(failure) && isIdentifierRefusal(toRefusal(failure))) dispatch({ type: 'problemFound' });
  };

  const request = (ask: CodeAsk, fromField: boolean): void => {
    if (inFlight(passcodeMutationKeys.all()) || liveHoldOf(latest().challenge.hold, askKeyOf(ask), Date.now()) !== null) return;
    const input: SendInput = { ...ask, epoch: latest().epoch, fromField };
    sender.mutate(input, {
      onSuccess: (sent) => {
        const accepted = acceptsSend(latest(), input.epoch, Date.now());
        dispatch({ type: 'codeSent', epoch: input.epoch, ask, sent, at: Date.now() });
        if (fromField && accepted) onCodeSent?.();
      },
      onError: (failure) => onRefused(failure, input),
    });
  };

  return {
    pending: sender.isPending,
    channel: sender.isPending ? sender.variables?.channel ?? null : null,
    paused: sender.isPaused,
    error: errorOf(sender, machine.state.epoch),
    request,
    retryLast: () => {
      const last = sender.variables;
      if (errorOf(sender, machine.state.epoch) === null || last === undefined) return false;
      request(askOf(last), last.fromField);
      return true;
    },
    clear: () => clearSettled(machine, sender, passcodeMutationKeys.send()),
  };
};
